import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navigation } from "./components/navigation";
import { PromotionalBanner } from "./components/promotionalbanner";
import { Header } from "./components/header";
import { Solutions } from "./components/solutions";
import { About } from "./components/about";
import { Services } from "./components/services";
import { Gallery } from "./components/gallery";
import { Testimonials } from "./components/testimonials";
import { Promos } from "./components/promos";
import BusinessWasteCalculator from "./components/ROICalculator";
import { Contact } from "./components/contact";
import { Footer } from "./components/Footer";
import TelematicsPage from "./components/TelematicsPage";
import PagesemblePage from "./components/PagesemblePage";
import ProcessPage from "./components/ProcessPage";
import RemoteAssetsPage from "./components/RemoteAssetsPage";
import Promo1Page from "./components/Promo1Page";
import Promo2Page from "./components/Promo2Page";
import Promo3Page from "./components/Promo3Page";
import ProtectedPromoRoute from "./components/ProtectedPromoRoute";
import AdminPanel from "./components/AdminPanel";
import SmsOptInPage from "./components/SmsOptInPage";
import UnsubscribePage from "./components/UnsubscribePage";
import NotFoundPage from "./components/NotFoundPage";
import CookieConsent from "./components/CookieConsent";
import Chatbot from "./components/Chatbot";
import CurrencyDebug from "./components/CurrencyDebug";
import JsonData from "./data/data.json";
import SeoConfig from "./data/seo-config.json";
import SmoothScroll from "smooth-scroll";
import { PROMO_FEATURES_ENABLED } from "./config/promoConfig";
import "./App.css";

export const scroll = new SmoothScroll('a[href*="#"]', {
  speed: 1000,
  speedAsDuration: true,
  offset: function(anchor, toggle) {
    // Check dynamic promo state first, fall back to static config
    const storedPromo = localStorage.getItem('promoEnabled');
    const isPromoActive = storedPromo !== null ? storedPromo === 'true' : PROMO_FEATURES_ENABLED;

    // Calculate offset for both main page (#menu) and solution pages (.telematics-header)
    const menuBar = document.getElementById('menu');
    const telematicsHeader = document.querySelector('.telematics-header');

    if (menuBar) {
      // Main landing page - restore original working offset for nav buttons
      const menuHeight = menuBar.offsetHeight;
      const bannerHeight = isPromoActive ? 50 : 0;
      return menuHeight + bannerHeight;
    } else if (telematicsHeader) {
      // Solution pages - with negative offset to scroll further down
      const headerHeight = telematicsHeader.offsetHeight;
      const bannerHeight = isPromoActive ? 50 : 0;
      return headerHeight + bannerHeight - 115;
    }

    // Fallback for any other pages
    const bannerHeight = isPromoActive ? 50 : 0;
    return 80 + bannerHeight;
  }
});

const LandingPage = () => {
  const [landingPageData, setLandingPageData] = useState({});

  const { meta, openGraph, twitter, jsonLd } = SeoConfig;
  const mainSchema = { "@context": "https://schema.org", ...jsonLd };
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["#header .intro-text h1", "#header .intro-text p", "#about .about-text p"]
    },
    "url": "https://yuccan.tech/"
  };
  const ogImage = openGraph.image || "https://yuccan.tech/img/hero_composite.jpg";
  const ogUrl = openGraph.url || "https://yuccan.tech";
  const ogSiteName = openGraph.siteName || "Yuccan Technologies";
  const twImage = twitter.image || "https://yuccan.tech/img/hero_composite.jpg";

  useEffect(() => {
    setLandingPageData(JsonData);
    
    // Check if there's a scroll target from promo pages
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      sessionStorage.removeItem('scrollTarget');
      
      // Wait for page to load completely, then scroll
      setTimeout(() => {
        const targetElement = document.querySelector(scrollTarget);
        if (targetElement) {
          // Calculate offset for /#section links - much smaller offset to match nav buttons
          const menuBar = document.getElementById('menu');
          const bannerHeight = PROMO_FEATURES_ENABLED ? 50 : 0;
          const menuHeight = menuBar ? menuBar.offsetHeight : 80;
          const totalOffset = menuHeight + bannerHeight;
          
          const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - totalOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 500); // Increased delay to ensure page is fully loaded
    }
  }, []);

  return (
    <div>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={(meta.keywords || []).join(", ")} />
        <link rel="canonical" href="https://yuccan.tech/" />
        <meta property="og:title" content={openGraph.title} />
        <meta property="og:description" content={openGraph.description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={ogUrl} />
        <meta property="og:type" content={openGraph.type || "website"} />
        <meta property="og:site_name" content={ogSiteName} />
        <meta name="twitter:card" content={twitter.card || "summary_large_image"} />
        <meta name="twitter:title" content={twitter.title} />
        <meta name="twitter:description" content={twitter.description} />
        <meta name="twitter:image" content={twImage} />
        {/* JSON-LD: Dynamic LocalBusiness from SEO engine (seo-config.json → updated weekly) */}
        <script type="application/ld+json">
          {JSON.stringify(mainSchema)}
        </script>

        {/* JSON-LD: Speakable — marks hero and about text for voice/AI reading */}
        <script type="application/ld+json">
          {JSON.stringify(speakableSchema)}
        </script>
      </Helmet>
      <PromotionalBanner />
      <Navigation />
      <Header data={landingPageData.Header} />
      <About data={landingPageData.About} partners={landingPageData.Partners} />
      <Services data={landingPageData.Services} />
      <Solutions data={landingPageData.Features} />
      <Gallery data={landingPageData.Gallery} />
      <Testimonials data={landingPageData.Testimonials} />
      <BusinessWasteCalculator />
      <Promos data={landingPageData.Promos} />
      <Contact data={landingPageData.Contact} />
      <Footer />
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <CookieConsent />
      <Chatbot />
      <CurrencyDebug />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/telematics" element={<TelematicsPage />} />
        <Route path="/pagesemble" element={<PagesemblePage />} />
        <Route path="/dynamic-pricing" element={<ProcessPage />} />
        <Route path="/remote-assets" element={<RemoteAssetsPage />} />
        <Route path="/promo1" element={<ProtectedPromoRoute><Promo1Page /></ProtectedPromoRoute>} />
        <Route path="/promo2" element={<ProtectedPromoRoute><Promo2Page /></ProtectedPromoRoute>} />
        <Route path="/promo3" element={<ProtectedPromoRoute><Promo3Page /></ProtectedPromoRoute>} />
        <Route path="/sms-opt-in" element={<SmsOptInPage />} />
        <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/notfound" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
};

export default App;
